import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, TextField } from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import IconAdd from '@mui/icons-material/Add';
import CustomButton from '../buttons/CustomButton';
import AttachmentList from '../patient-attachments/AttachmentList';
import AttachmentViewerPopup from '../patient-attachments/AttachmentViewerPopup';
import PatientAttachmentUploadPopup from '../../popup/PatientAttachmentUploadPopup/PatientAttachmentUploadPopup';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './styles.module.css';

/** Contextos de anexo dos documentos de convênio (contextId = id do documento). */
export const MEDICAL_REQUEST_CONTEXT = 'medical_request';
export const INSURANCE_AUTHORIZATION_CONTEXT = 'insurance_authorization';

const ACCEPT = 'image/*,application/pdf';

// Na lista, cada anexo aparece pelo nome do arquivo: o título costuma repetir o do documento
const byFileName = (item) => ({ ...item, title: item.originalName || item.title });

/**
 * Anexos de um pedido médico ou de uma guia (PDF ou foto): lista, envio e
 * visualizador, reaproveitando os componentes de anexos da pessoa. A API lista
 * por `context`; o filtro por `contextId` fica aqui, porque o endpoint não o
 * recebe. `onChanged` avisa quem mostra o contador "N anexos".
 */
function InsuranceDocumentAttachments({ patientId, context, contextId, subject, enabled = true, onChanged }) {
  const { ListPatientAttachments } = DataProviderInstance();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    if (!patientId || !contextId) return;
    ListPatientAttachments(patientId, context, (response) => {
      if (response && response.status === 'SUCCESS') {
        const mine = (response.data || []).filter((item) => String(item.contextId) === String(contextId));
        setRows(mine);
        setError('');
      } else {
        setError(response?.message || response?.error || 'Não foi possível carregar os anexos.');
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, context, contextId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const changed = () => {
    load();
    if (onChanged) onChanged();
  };

  const renderItemFields = (file, value, onChange) => (
    <div className={styles.itemFields}>
      <TextField
        size="small"
        label="Título"
        value={(value && value.title) || ''}
        onChange={(event) => onChange({ ...(value || {}), title: event.target.value })}
      />
    </div>
  );

  const buildItemPayload = (value) => ({ title: ((value && value.title) || '').trim() });

  const count = rows.length;
  const countLabel = count === 0 ? 'Nenhum anexo' : `${count} ${count === 1 ? 'anexo' : 'anexos'}`;

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.subtitle}>
          {subject && <span className={styles.subject} title={subject}>{subject}</span>}
          <span className={styles.count}>{countLabel}</span>
        </div>
        {enabled && (
          <CustomButton
            variant="contained"
            style="main"
            label="Anexar"
            icon={<IconAdd />}
            onClick={() => setShowUpload(true)}
          />
        )}
      </div>

      {error && <div className={styles.error} role="alert">{error}</div>}

      {loading ? (
        <div className={styles.loading}><CircularProgress size={24} /></div>
      ) : count === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}><AttachFileOutlinedIcon /></span>
          <span>Nenhum arquivo ainda. Anexe o PDF ou a foto do documento.</span>
        </div>
      ) : (
        <AttachmentList list={rows.map(byFileName)} groupByCategory={false} onSelect={(item) => setSelected(rows.find((row) => row.id === item.id) || item)} />
      )}

      {showUpload && (
        <PatientAttachmentUploadPopup
          patientId={patientId}
          context={context}
          contextId={contextId}
          capture
          accept={ACCEPT}
          maxFiles={5}
          title="Anexar PDF ou foto"
          renderItemFields={renderItemFields}
          buildItemPayload={buildItemPayload}
          OnHide={(success) => {
            setShowUpload(false);
            if (success) changed();
          }}
        />
      )}

      {selected && (
        <AttachmentViewerPopup
          attachment={selected}
          list={rows}
          enabled={enabled}
          OnHide={(didChange) => {
            setSelected(null);
            if (didChange) changed();
          }}
        />
      )}
    </div>
  );
}

export default InsuranceDocumentAttachments;
